import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Pagination,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import axios from 'axios';
import './UserDetails.css';
import { FaPlus } from 'react-icons/fa';
import { BiPlus } from 'react-icons/bi';
import AddUserModal from '../AddUserModal/AddUserModal';
import ColumnFilter from '../../../components/ColumnFilter/ColumnFilter';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Role configurations with colors and labels
const ROLE_CONFIG = {
  branches: {
    color: '#4285F4',
    label: 'Total Branches',
    description: 'All branch locations',
    icon: '🏢',
  },
  ro: {
    color: '#34A853',
    label: 'Regional Officers',
    description: 'RO personnel',
    icon: '👤',
  },
  bm: {
    color: '#FBBC04',
    label: 'Branch Managers',
    description: 'BM personnel',
    icon: '👨‍💼',
  },
  bcm: {
    color: '#EA4335',
    label: 'Branch Credit Managers',
    description: 'BCM personnel',
    icon: '📊',
  },
};

// Helper to read query params
const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const UserDetailsPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const query = useQuery();

  const pageParam = Number(query.get('page') || 1);
  const searchParam = query.get('search') || '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [branchData, setBranchData] = useState(null);
  const [detailSearch, setDetailSearch] = useState(searchParam);
  const [page, setPage] = useState(pageParam);
  const ITEMS_PER_PAGE = 10;

  const currentConfig = ROLE_CONFIG[type] || ROLE_CONFIG.branches;

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [preSelectedRole, setPreSelectedRole] = useState('');

  const [columnFilters, setColumnFilters] = useState({
    emp_id: "",
    name: "",
    email: "",
    branch_code: "",
    branch_name: "",
    state_name: "",
  });

  const [branchColumnFilters, setBranchColumnFilters] = useState({
    branch_code: "",
    branch_name: "",
    state_name: ""
  });

  const updateColumnFilter = (field, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1);  // ⭐ Reset to page 1 whenever a column filter is applied
  };

  const updateBranchColumnFilter = (field, value) => {
    setBranchColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1);  // ⭐ Reset to page 1 whenever a column filter is applied
  };

  const applyColumnFilters = (data) => {
    return data.filter(item => {
      return (
        (!columnFilters.emp_id || (item.emp_id || "").toLowerCase().includes(columnFilters.emp_id.toLowerCase())) &&
        (!columnFilters.name || (`${item.first_name} ${item.last_name}`.trim()).toLowerCase().includes(columnFilters.name.toLowerCase())) &&
        (!columnFilters.email || (item.email || "").toLowerCase().includes(columnFilters.email.toLowerCase())) &&
        (!columnFilters.branch_code || (item.branch_code || "").toLowerCase().includes(columnFilters.branch_code.toLowerCase())) &&
        (!columnFilters.branch_name || (item.branch_name || "").toLowerCase().includes(columnFilters.branch_name.toLowerCase())) &&
        (!columnFilters.state_name ||
          (item.state_name
            .replace(/[_-]/g, " ")         // turn "_" and "-" into spaces
            // .replace(/\s+/g, " ")           // replace multiple spaces with single space
            .trim() || '').toLowerCase().includes(columnFilters.state_name.toLowerCase()))
      );
    });
  };

  const fetchBranchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const tokens = JSON.parse(sessionStorage.getItem('tokens')) || '';
      // Fetch all data with high limit
      const res = await axios.get(
        `${API_BASE_URL}/officer/get-branch-officer-report`,
        {
          params: { page: 1, limit: 20000, is_download: false },
          headers: { Authorization: `Bearer ${tokens.token}`, accept: '*/*' },
        }
      );
      if (res?.data?.status) {
        setBranchData(res.data.data);
      } else {
        setBranchData({ officers: [], branch_report: {} });
      }
    } catch (err) {
      console.error('Details fetch error', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL branch report data once
  useEffect(() => {
    let mounted = true;
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const tokens = JSON.parse(sessionStorage.getItem('tokens')) || '';
        // Fetch all data with high limit
        const res = await axios.get(
          `${API_BASE_URL}/officer/get-branch-officer-report`,
          {
            params: { page: 1, limit: 20000, is_download: false },
            headers: { Authorization: `Bearer ${tokens.token}`, accept: '*/*' },
          }
        );
        if (!mounted) return;
        if (res?.data?.status) {
          setBranchData(res.data.data);
        } else {
          setBranchData({ officers: [], branch_report: {} });
        }
      } catch (err) {
        console.error('Details fetch error', err);
        if (mounted) {
          setError(
            err.response?.data?.message || err.message || 'Failed to load data'
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      mounted = false;
    };
  }, []); // Only fetch once on mount

  // Update URL when search or pagination changes
  useEffect(() => {
    const qs = new URLSearchParams();
    if (page > 1) qs.set('page', page);
    if (detailSearch) qs.set('search', detailSearch);
    navigate(
      { pathname: `/user-management/details/${type}`, search: qs.toString() },
      { replace: true }
    );
  }, [page, detailSearch, type, navigate]);

  // Flatten officers by role
  const flattenOfficersByRole = (roleCode) => {
    const out = [];
    (branchData?.officers || []).forEach((b) => {
      (b.officer_details || []).forEach((o) => {
        if (o.role_code === roleCode) {
          out.push({
            ...o,
            branch_code: b.branch_code,
            branch_name: b.branch_name,
            state_name: b.state_name,
          });
        }
      });
    });
    return out.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const onBack = () => navigate('/user-management');

  // Filter functions
  // const filterBranches = (arr) => {
  //   if (!detailSearch) return arr;
  //   const q = detailSearch.toLowerCase();
  //   return arr.filter(
  //     (b) =>
  //       (b.branch_code || '').toLowerCase().includes(q) ||
  //       (b.branch_name || '').toLowerCase().includes(q) ||
  //       (b.state_name || '').toLowerCase().includes(q)
  //   );
  // };

  const filterBranches = (arr) => {
    let result = arr;

    // global search (already inside your code)
    if (detailSearch) {
      const q = detailSearch.toLowerCase();

      result = result.filter(b =>
        (b.branch_code || "").toLowerCase().includes(q) ||
        (b.branch_name || "").toLowerCase().includes(q) ||
        (b.state_name
          .replace(/[_-]/g, " ")         // turn "_" and "-" into spaces
          // .replace(/\s+/g, " ")           // replace multiple spaces with single space
          .trim() || '').toLowerCase().includes(q)
      );
    }

    // Branch column filters ⭐
    return result.filter(b => {
      return (
        (!branchColumnFilters.branch_code ||
          (b.branch_code || "")
            .toLowerCase()
            .includes(branchColumnFilters.branch_code.toLowerCase())) &&

        (!branchColumnFilters.branch_name ||
          (b.branch_name || "")
            .toLowerCase()
            .includes(branchColumnFilters.branch_name.toLowerCase())) &&

        (!branchColumnFilters.state_name ||
          (b.state_name
            .replace(/[_-]/g, " ")         // turn "_" and "-" into spaces
            // .replace(/\s+/g, " ")           // replace multiple spaces with single space
            .trim() || '')
            .toLowerCase()
            .includes(branchColumnFilters.state_name.toLowerCase()))
      );
    });
  };


  const filterOfficers = (arr) => {
    if (!detailSearch) return arr;
    const q = detailSearch.toLowerCase()
      // .replace(/[^a-z0-9\s]/g, "")    // REMOVE all special characters
      .replace(/[_-]/g, " ")         // turn "_" and "-" into spaces
      .replace(/\s+/g, " ")           // replace multiple spaces with single space
      .trim();

    return arr.filter(
      (o) =>
        (o.first_name || '').toLowerCase().includes(q) ||
        (o.last_name || '').toLowerCase().includes(q) ||
        (o.emp_id || '').toLowerCase().includes(q) ||
        (o.branch_code || '').toLowerCase().includes(q) ||
        (o.branch_name || '').toLowerCase().includes(q) ||
        (o.email || '').toLowerCase().includes(q) ||
        (o.state_name
          .replace(/[_-]/g, " ")         // turn "_" and "-" into spaces
          // .replace(/\s+/g, " ")           // replace multiple spaces with single space
          .trim() || '').toLowerCase().includes(q)
    );
  };

  // Get filtered data based on type
  const getFilteredData = () => {
    // if (type === 'branches') {
    //   return filterBranches(branchData?.officers || []);
    // } else if (type === 'ro') {
    //   return filterOfficers(flattenOfficersByRole('RO'));
    // } else if (type === 'bm') {
    //   return filterOfficers(flattenOfficersByRole('BM'));
    // } else if (type === 'bcm') {
    //   return filterOfficers(flattenOfficersByRole('BCM'));
    // }
    // return [];


    let data = [];

    if (type === 'ro') data = flattenOfficersByRole("RO");
    else if (type === 'bm') data = flattenOfficersByRole("BM");
    else if (type === 'bcm') data = flattenOfficersByRole("BCM");
    else if (type === 'branches') return filterBranches(branchData?.officers || []);

    // global search
    let filtered = filterOfficers(data);

    // column filters
    filtered = applyColumnFilters(filtered);

    return filtered;
  };

  // Get paginated data for current page
  const getPaginatedData = () => {
    const filteredData = getFilteredData();
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  };

  // Calculate total count
  const getTotalCount = () => {
    return getFilteredData().length;
  };

  // Calculate total pages
  const getTotalPages = () => {
    const totalCount = getTotalCount();
    return Math.ceil(totalCount / ITEMS_PER_PAGE) || 1;
  };

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [detailSearch]);

  // Render branches table
  const renderBranchesTable = () => {
    const items = getPaginatedData();
    return (
      <div className="custom-table-wrapper">
        <Table striped hover responsive className="custom-table">
          <thead>
            <tr>
              <th>#</th>
              <th>
                Branch Code
                <ColumnFilter
                  placeholder="Branch Code"
                  value={branchColumnFilters.branch_code}
                  onChange={(v) => updateBranchColumnFilter("branch_code", v)}
                />
              </th>
              <th>
                Branch Name
                <ColumnFilter
                  placeholder="Branch Name"
                  value={branchColumnFilters.branch_name}
                  onChange={(v) => updateBranchColumnFilter("branch_name", v)}
                />
              </th>
              <th>
                State
                <ColumnFilter
                  placeholder="State"
                  value={branchColumnFilters.state_name}
                  onChange={(v) => updateBranchColumnFilter("state_name", v)}
                />
              </th>
              <th className="text-center">
                Total Officers
              </th>
              <th className="text-center">
                RO
              </th>
              <th className="text-center">
                BM
              </th>
              <th className="text-center">
                BCM
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  <div className="empty-state">
                    <span className="empty-icon">🔍</span>
                    <p>No branches found</p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((b, i) => (
                <tr key={b.branch_code || i}>
                  <td className="row-number">
                    {(page - 1) * ITEMS_PER_PAGE + i + 1}
                  </td>
                  <td>
                    <Badge bg="secondary" className="code-badge">
                      {b.branch_code || '-'}
                    </Badge>
                  </td>
                  <td className="fw-medium">{b.branch_name || '-'}</td>
                  <td>
                    <span className="state-tag">
                      {(b.state_name || '-').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="text-center">
                    <Badge bg="info">{b.total_officers || 0}</Badge>
                  </td>
                  <td className="text-center">
                    <Badge bg="success">{b.ro_count || 0}</Badge>
                  </td>
                  <td className="text-center">
                    <Badge bg="warning" text="dark">
                      {b.bm_count || 0}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Badge bg="danger">{b.bcm_count || 0}</Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render role table
  const renderRoleTable = () => {
    const items = getPaginatedData();
    return (
      <div className="custom-table-wrapper">
        <Table striped hover responsive className="custom-table">
          <thead>
            <tr>
              <th>#</th>
              <th>
                Employee ID
                <ColumnFilter
                  placeholder="Employee ID"
                  value={columnFilters.emp_id}
                  onChange={(v) => updateColumnFilter("emp_id", v)}
                />
              </th>
              <th>
                Name
                <ColumnFilter
                  placeholder="Name"
                  value={columnFilters.name}
                  onChange={(v) => updateColumnFilter("name", v)}
                />
              </th>
              <th>
                Email
                <ColumnFilter
                  placeholder="Email"
                  value={columnFilters.email}
                  onChange={(v) => updateColumnFilter("email", v)}
                />
              </th>
              <th>
                Branch Code
                <ColumnFilter
                  placeholder="Branch Code"
                  value={columnFilters.branch_code}
                  onChange={(v) => updateColumnFilter("branch_code", v)}
                />
              </th>
              <th>
                Branch Name
                <ColumnFilter
                  placeholder="Branch Name"
                  value={columnFilters.branch_name}
                  onChange={(v) => updateColumnFilter("branch_name", v)}
                />
              </th>
              <th>
                State
                <ColumnFilter
                  placeholder="State"
                  value={columnFilters.state_name}
                  onChange={(v) => updateColumnFilter("state_name", v)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  <div className="empty-state">
                    <span className="empty-icon">👤</span>
                    <p>No {type?.toUpperCase()} officers found</p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((o, i) => (
                <tr key={o.officer_id || o.emp_id || i}>
                  <td className="row-number">
                    {(page - 1) * ITEMS_PER_PAGE + i + 1}
                  </td>
                  <td>
                    <Badge bg="secondary" className="code-badge">
                      {o.emp_id || '-'}
                    </Badge>
                  </td>
                  <td className="fw-medium" style={{ wordBreak: "break-all", whiteSpace: "normal", overflowWrap: "break-word", }}>
                    {`${o.first_name || ''} ${o.last_name || ''}`.trim() || '-'}
                  </td>
                  <td className="email-cell">{o.email || '-'}</td>
                  <td>
                    <Badge bg="light" text="dark" className="code-badge">
                      {o.branch_code || '-'}
                    </Badge>
                  </td>
                  <td>{o.branch_name || '-'}</td>
                  <td>
                    <span className="state-tag">
                      {(o.state_name || '-').replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const totalPages = getTotalPages();
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('ellipsis');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('ellipsis');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const openAddUserModal = (branch = null, role = '') => {
    setSelectedBranch(branch);
    setPreSelectedRole(role);
    setShowAddUserModal(true);
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    setSelectedBranch(null);
    setPreSelectedRole('');

    // fetchBranchData(currentPage);
  };

  return (
    <Container fluid className="user-details-page">
      {/* Header Section */}
      <div
        className="page-header"
        style={{ borderLeftColor: currentConfig.color }}
      >
        <Row className="align-items-center">
          <Col xs="auto" className='d-flex align-items-center gap-4'>
            <Button variant="outline-primary" onClick={onBack} size="lg">
              ← Back to Dashboard
            </Button>
            {type !== "branches" && <Button
              className='search-btn d-flex align-items-center gap-2'
              onClick={() => {
                openAddUserModal(null, type?.toUpperCase())
              }}
            >
              <BiPlus size={"20"} />
              Add User
            </Button>}
          </Col>
          <Col>
            <div className="header-content-right">
              <span
                className="header-icon"
                style={{ color: currentConfig.color }}
              >
                {currentConfig.icon}
              </span>
              <div>
                <h3 className="page-title">{currentConfig.label}</h3>
                <p className="page-subtitle">{currentConfig.description}</p>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Search Section */}
      <Card className="filter-card mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={12}>
              <Form.Group>
                <Form.Control
                  type="search"
                  placeholder="🔍 Search by name, code, email, or state..."
                  value={detailSearch}
                  onChange={(e) => {
                    setDetailSearch(e.target.value);
                  }}
                  className="search-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Content Card with Color Accent */}
      <Card
        className="data-card"
        style={{ borderLeftColor: currentConfig.color }}
      >
        <Card.Header
          className="data-card-header"
          style={{ backgroundColor: `${currentConfig.color}15` }}
        >
          <h5 className="mb-0" style={{ color: currentConfig.color }}>
            {type === 'branches'
              ? 'Branch Directory'
              : `${type?.toUpperCase()} Personnel List`}
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading && (
            <div className="loading-state">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading data...</p>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="m-3">
              <Alert.Heading>Error Loading Data</Alert.Heading>
              <p className="mb-0">{error}</p>
            </Alert>
          )}

          {!loading && !error && (
            <>
              {type === 'branches' && renderBranchesTable()}
              {(type === 'ro' || type === 'bm' || type === 'bcm') &&
                renderRoleTable()}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Numbered Pagination - Centered */}
      {!loading && !error && getTotalCount() > 0 && (
        <div className="pagination-wrapper-centered">
          <Pagination className="custom-pagination-centered">
            <Pagination.First onClick={() => setPage(1)} disabled={page <= 1} />
            <Pagination.Prev
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            />

            {generatePageNumbers().map((pageNum, index) =>
              pageNum === 'ellipsis' ? (
                <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
              ) : (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              )
            )}

            <Pagination.Next
              onClick={() => setPage(Math.min(getTotalPages(), page + 1))}
              disabled={page >= getTotalPages()}
            />
            <Pagination.Last
              onClick={() => setPage(getTotalPages())}
              disabled={page >= getTotalPages()}
            />
          </Pagination>
        </div>
      )}

      {showAddUserModal && (
        <AddUserModal
          closeModal={closeAddUserModal}
          selectedBranch={selectedBranch}
          preSelectedRole={preSelectedRole}
          onSuccess={() => {
            fetchBranchReport();     // 🔥 REFRESH TABLE DATA
            setPage(1);        // optional: go back to first page
          }}
        />
      )}
    </Container>
  );
};

export default UserDetailsPage;
